import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Smartphone, 
  RefreshCw, 
  CheckCircle, 
  XCircle, 
  Wifi, 
  WifiOff,
  LogOut,
  QrCode
} from 'lucide-react';
import whatsappService from '../services/whatsappService';
import toast from 'react-hot-toast';
import QRCode from 'qrcode';

interface ConnectionStatus {
  status: 'connected' | 'disconnected';
  authenticated: boolean;
  phoneNumber: string | null;
  hasQR: boolean;
  error: string | null;
}

const WhatsAppConnect: React.FC = () => {
  const [status, setStatus] = useState<ConnectionStatus | null>(null);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [qrImageUrl, setQrImageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const fetchStatus = async () => {
    try {
      const statusData = await whatsappService.getStatus();
      setStatus(statusData);
      
      if (statusData.hasQR && !statusData.authenticated) {
        const qr = await whatsappService.getQRCode();
        if (qr) {
          setQrCode(qr);
          // Convert QR string to image
          const imageUrl = await QRCode.toDataURL(qr, {
            width: 300,
            margin: 2,
            color: {
              dark: '#128C7E',
              light: '#FFFFFF'
            }
          });
          setQrImageUrl(imageUrl);
        }
      } else {
        setQrCode(null);
        setQrImageUrl(null);
      }
    } catch (error) {
      console.error('Error fetching status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    
    // Poll for status updates every 3 seconds
    const interval = setInterval(fetchStatus, 3000);
    
    return () => clearInterval(interval);
  }, []);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchStatus();
    setIsRefreshing(false);
    toast.success('Stato aggiornato');
  };

  const handleLogout = async () => {
    const confirmed = window.confirm('Sei sicuro di voler disconnettere WhatsApp?');
    if (confirmed) {
      const success = await whatsappService.logout();
      if (success) {
        setStatus(null);
        setQrCode(null);
        setQrImageUrl(null);
        fetchStatus();
      }
    }
  };

  const handleRestart = async () => {
    toast.loading('Riavvio client WhatsApp...');
    const success = await whatsappService.restart();
    if (success) {
      setQrCode(null);
      setQrImageUrl(null);
      setTimeout(fetchStatus, 3000);
    }
  };

  const isConnected = status?.status === 'connected' && status?.authenticated;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-6"
      >
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-gradient-to-br from-whatsapp-green to-emerald-600 rounded-xl">
              <Smartphone className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Connessione WhatsApp
              </h1>
              <p className="text-gray-500 dark:text-gray-400">
                Collega il tuo account WhatsApp per le automazioni
              </p>
            </div>
          </div>
          
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
          >
            <RefreshCw className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Status Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className={`p-6 rounded-2xl border-2 ${
            isConnected 
              ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' 
              : 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800'
          }`}
        >
          <div className="flex items-center gap-4">
            <div className={`p-4 rounded-full ${
              isConnected 
                ? 'bg-green-100 dark:bg-green-800' 
                : 'bg-orange-100 dark:bg-orange-800'
            }`}>
              {isConnected ? (
                <Wifi className="w-8 h-8 text-green-600 dark:text-green-400" />
              ) : (
                <WifiOff className="w-8 h-8 text-orange-600 dark:text-orange-400" />
              )}
            </div>
            
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h2 className={`text-xl font-semibold ${
                  isConnected 
                    ? 'text-green-800 dark:text-green-300' 
                    : 'text-orange-800 dark:text-orange-300'
                }`}>
                  {isConnected ? 'Connesso' : 'Non connesso'}
                </h2>
                {isConnected ? (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                ) : (
                  <XCircle className="w-5 h-5 text-orange-600" />
                )}
              </div>
              
              {status?.phoneNumber && (
                <p className="text-green-700 dark:text-green-400 mt-1">
                  📱 Numero: +{status.phoneNumber}
                </p>
              )}
              
              {status?.error && (
                <p className="text-red-600 dark:text-red-400 mt-1 text-sm">
                  ⚠️ {status.error}
                </p>
              )}
            </div>

            {isConnected && (
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                Disconnetti
              </button>
            )}
          </div>
        </motion.div>

        {/* QR Code Section */}
        {!isConnected && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
          >
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <QrCode className="w-6 h-6 text-whatsapp-green" />
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Scansiona il QR Code
                </h2>
              </div>
              <p className="text-gray-500 dark:text-gray-400 mt-1 text-sm">
                Apri WhatsApp sul telefono → Menu → Dispositivi collegati → Collega un dispositivo
              </p>
            </div>

            <div className="p-8 flex flex-col items-center">
              {isLoading ? (
                <div className="w-[300px] h-[300px] flex items-center justify-center bg-gray-100 dark:bg-gray-700 rounded-xl">
                  <RefreshCw className="w-12 h-12 text-gray-400 animate-spin" />
                </div>
              ) : qrImageUrl ? (
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="relative"
                >
                  <img 
                    src={qrImageUrl} 
                    alt="WhatsApp QR Code" 
                    className="w-[300px] h-[300px] rounded-xl shadow-lg"
                  />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center shadow-lg">
                      <Smartphone className="w-10 h-10 text-whatsapp-green" />
                    </div>
                  </div>
                </motion.div>
              ) : (
                <div className="w-[300px] h-[300px] flex flex-col items-center justify-center bg-gray-100 dark:bg-gray-700 rounded-xl">
                  <QrCode className="w-16 h-16 text-gray-400 mb-4" />
                  <p className="text-gray-500 dark:text-gray-400 text-center">
                    QR Code non disponibile
                  </p>
                  <button
                    onClick={handleRestart}
                    className="mt-4 px-4 py-2 bg-whatsapp-green text-white rounded-lg hover:bg-whatsapp-dark transition-colors"
                  >
                    Riavvia Client
                  </button>
                </div>
              )}

              <p className="mt-6 text-center text-gray-500 dark:text-gray-400 text-sm max-w-md">
                Il QR code si aggiorna automaticamente ogni 20 secondi. 
                Una volta scansionato, la connessione verrà stabilita automaticamente.
              </p>
            </div>
          </motion.div>
        )}

        {/* Instructions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-6 border border-blue-200 dark:border-blue-800"
        >
          <h3 className="font-semibold text-blue-900 dark:text-blue-300 mb-3">
            📋 Come collegare WhatsApp
          </h3>
          <ol className="space-y-2 text-blue-800 dark:text-blue-400 text-sm">
            <li className="flex items-start gap-2">
              <span className="font-bold">1.</span>
              Apri WhatsApp sul tuo telefono
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold">2.</span>
              Vai su <strong>Impostazioni</strong> → <strong>Dispositivi collegati</strong>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold">3.</span>
              Tocca <strong>Collega un dispositivo</strong>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold">4.</span>
              Inquadra il QR code mostrato sopra
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold">5.</span>
              Attendi la conferma di connessione
            </li>
          </ol>
          
          <div className="mt-4 p-3 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
            <p className="text-yellow-800 dark:text-yellow-300 text-sm">
              ⚠️ <strong>Nota:</strong> La sessione rimarrà attiva anche dopo aver chiuso l'app. 
              Per disconnettere, usa il pulsante "Disconnetti" o rimuovi il dispositivo da WhatsApp.
            </p>
          </div>
        </motion.div>

        {/* Connected Features */}
        {isConnected && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-4"
          >
            <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <span className="font-medium text-gray-900 dark:text-white">Messaggi</span>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Puoi inviare e ricevere messaggi
              </p>
            </div>
            
            <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <span className="font-medium text-gray-900 dark:text-white">Gruppi</span>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Accesso completo ai gruppi WhatsApp
              </p>
            </div>
            
            <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <span className="font-medium text-gray-900 dark:text-white">Automazioni</span>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                I flow sono attivi e in ascolto
              </p>
            </div>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

export default WhatsAppConnect;
